import React, { useRef, useEffect, useContext } from "react"
import { useTranslation } from "react-i18next"
import { SiteContext } from "@context/Provider"

import useOutsideClick from "@atoms/UseOutsideClick/UseOutsideClick"
import Heading from "@atoms/Heading/Heading"
import SearchBar from "@molecules/SearchBar/SearchBar"

import {
  useSearchContext,
  getInitialStateFromSearchParam,
} from "@context/search"
import {
  LOCATION_LABEL,
  UNIT_TYPE_LABEL,
  AREA_LABEL,
  AVAILABILITY_LABEL,
} from "@helpers/constants"

import {
  SET_INITIAL_STATE,
  AREA_CHANGE,
  TYPE_CHANGE,
  UNIT_AREA_CHANGE,
  AVAILABILITY_CHANGE,
  HIGHLIGHT_FILTERS,
} from "@context/search/actions"

import { useSiteAndUnitTaxonomies } from "@context/search/queries"

const SearchResultControls = () => {
  const { t } = useTranslation()
  const {
    state: { availability },
    dispatch,
  } = useSearchContext()
  const {
    wp: {
      themeSettings: {
        themeSettings: {
          groupGlobalPageLinks: { mainSiteSearch },
        },
      },
    },
  } = useSiteAndUnitTaxonomies()

  const context = useContext(SiteContext)

  useEffect(() => {
    const searchState = getInitialStateFromSearchParam()

    if (searchState) {
      dispatch({ type: SET_INITIAL_STATE, payload: { ...searchState } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBodyNoSCrollClasses = (action) => {
    const classNames = {
      add: ["overflow-hidden", "xl:overflow-auto"],
      remove: ["overflow-hidden", "xl:overflow-auto"],
    }

    setTimeout(() => {
      // Set timeout is needed because Safari pushes the scroll check on top after this and nothing happens
      action === "add"
        ? document.body.classList.add(...classNames.add)
        : action === "remove"
        ? document.body.classList.remove(...classNames.remove)
        : console.error("Invalid argument passed to handleBodyNoSCrollClasses")
    }, 200)
  }

  const handleMenu = (highlight) => {
    dispatch({ type: HIGHLIGHT_FILTERS, payload: highlight })
    handleBodyNoSCrollClasses(highlight ? "add" : "remove")
  }

  const handleOnChange = (value, query) => {
    const { action, removedValues } = query
    let type = value?.type
    if ("clear" === action && removedValues?.length) {
      type = removedValues[0]?.type
    }

    dispatch({ type: HIGHLIGHT_FILTERS, payload: false })
    handleBodyNoSCrollClasses("remove")
    let actionType
    switch (type) {
      case LOCATION_LABEL():
        actionType = AREA_CHANGE
        break
      case UNIT_TYPE_LABEL():
        actionType = TYPE_CHANGE
        break
      case AREA_LABEL():
        actionType = UNIT_AREA_CHANGE
        break
      case AVAILABILITY_LABEL():
        actionType = AVAILABILITY_CHANGE
        break
      default:
        actionType = null
    }

    // We shouldn't dispatch the availability event twice, but the other filters can trigger it twice.
    // The reason for this is that the AVAILABILITY_CHANGE event causes navigation/refresh,
    // which we don't want to happen when we're simply updating the availability.
    if (actionType && actionType !== AVAILABILITY_CHANGE) {
      dispatch({
        type: actionType,
        payload: value,
      })
    }

    const actualAvailabilityValue =
      actionType === AVAILABILITY_CHANGE ? value : availability

    dispatch({
      type: AVAILABILITY_CHANGE,
      payload: {
        ...actualAvailabilityValue,
        redirectSlug: mainSiteSearch.slug,
      },
    })
  }

  const wrapperRef = useRef()

  useOutsideClick(wrapperRef, () => {
    dispatch({ type: HIGHLIGHT_FILTERS, payload: false })
    handleBodyNoSCrollClasses("remove")
  })

  const areaAndUnitType = context?.title

  return (
    <div className="bg-search-image bg-cover bg-no-repeat">
      <div className="max-w-screen-lg px-3 lg:mx-auto py-7 lg:py-10 flex justify-center flex-wrap">
        <Heading
          text={`${t("Search results")}: ${areaAndUnitType}`}
          level={1}
          visualLevel={2}
          textColor="text-white"
          customClasses="mb-4 lg:mb-7 w-full"
          align="center"
        />
        <div ref={wrapperRef} className={"w-full"}>
          <SearchBar handleMenu={handleMenu} handleOnChange={handleOnChange} />
        </div>
      </div>
    </div>
  )
}

export default SearchResultControls
